/** Type for class constructor references used in validation */
type Constructor = new (...args: unknown[]) => unknown;

/**
 * Base class that provides validation logic for classes that require certain methods to be implemented.
 * Subclasses should define their own static REQUIRED_METHODS array and pass it to super() in the constructor.
 */
export abstract class BaseValidatedClass {
  /** Set to track validated classes for performance optimization */
  private static readonly validatedClasses = new Set<Constructor>();

  /** Map to store validation errors for each class */
  private static readonly validationErrors = new Map<Constructor, string[]>();

  /**
   * Validates that all required methods are properly implemented in the subclass.
   * @param requiredMethods - Array of method names that must be implemented
   * @param classRef - Reference to the class constructor for validation caching
   */
  constructor(requiredMethods: string[], classRef: Constructor) {
    // Only validate subclasses, not the base class itself
    if ((classRef as unknown) !== BaseValidatedClass) {
      // Validate only once per class type for performance
      if (!BaseValidatedClass.validatedClasses.has(classRef)) {
        this.validateImplementation(requiredMethods, classRef);
      }

      // Check if this class has validation errors from previous validation
      const errors = BaseValidatedClass.validationErrors.get(classRef);
      if (errors && errors.length > 0) {
        throw new Error(
          `${classRef.name} has validation errors:\n${errors.map((e: string) => `  - ${e}`).join('\n')}`,
        );
      }
    }
  }

  /**
   * Validates that all required methods are properly implemented in the subclass.
   * This validation runs only once per class type and results are cached.
   */
  private validateImplementation(requiredMethods: string[], classRef: Constructor): void {
    const errors: string[] = [];
    const className = classRef.name;
    const proto = Object.getPrototypeOf(this);

    // Check each required method
    requiredMethods.forEach((methodName) => {
      // Check if the method exists and is a function
      const method = (this as Record<string, unknown>)[methodName];
      if (typeof method !== 'function') {
        errors.push(`Method ${methodName}() is not defined`);
        return;
      }

      // Check if method is still using the base class implementation
      // (which throws "must be implemented" error)
      if (proto[methodName] === classRef.prototype[methodName]) {
        errors.push(`Method ${methodName}() must be implemented (currently using base class error-throwing implementation)`);
      }
    });

    // Mark this class as validated
    BaseValidatedClass.validatedClasses.add(classRef);

    // Store errors if any were found
    if (errors.length > 0) {
      BaseValidatedClass.validationErrors.set(classRef, errors);
      // Log validation errors for debugging
      console.error(`[${className} Validation] ${className} validation failed:`, errors);
    } else {
      // Log successful validation in development
      if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log(`[${className} Validation] ✅ ${className} validated successfully`);
      }
    }
  }

  /**
   * Lifecycle method for cleaning up resources (e.g., removing DOM artifacts from document.body).
   * Override this method in subclasses to implement custom cleanup logic.
   * Called when the editor is reinitialized or the extension is uninstalled.
   */
  public destroy(): void {
    // No-op by default - subclasses can override to clean up their resources
  }
}
